import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../shared';

const PrivacyPolicy = () => (
  <div className="container px-md-4 py-md-4 page">
    <Seo
      title="Privacy Policy"
      path="/privacy"
      description="How Visit All Nepal collects, uses, and protects your information when you use our travel guides and booking services."
    />
    <div className="legal-page card border-0 shadow-sm p-4 p-md-5">
      <h1 className="title mb-4">Privacy Policy</h1>
      <p className="text-muted small">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

      <p>
        Visit All Nepal (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates visitallnepal.com — Nepal travel guides, trip-planning tools, and booking for group departures, private tours, and shared rides. This Privacy Policy explains how we collect, use, share, and protect your information when you use our platform.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">1. Information We Collect</h2>
      <p>
        We collect information you provide directly and information we get when you use our services:
      </p>
      <ul className="legal-list pl-4">
        <li><strong>Account and profile:</strong> name, email, phone number, and password when you sign up or log in.</li>
        <li><strong>Booking and inquiries:</strong> travel dates, number of travelers, names and contact details, special requests, and similar information when you reserve a group departure, request a private tour, or submit an inquiry.</li>
        <li><strong>Payment:</strong> we do not store full card numbers; payment is processed by third-party payment providers. We may retain transaction references, amounts, and status needed for your bookings and our records.</li>
        <li><strong>Communications:</strong> when you contact us (e.g. via chat, email, or support), we keep records of those exchanges.</li>
        <li><strong>Usage and device:</strong> IP address, browser type, device information, and how you use our site (e.g. pages visited, actions taken) to improve our services and security. We may use cookies and similar technologies as described below.</li>
      </ul>

      <h2 className="h5 text-primary mt-4 mb-2">2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul className="legal-list pl-4">
        <li>Create and manage your account, and authenticate you.</li>
        <li>Process and fulfil your bookings, and share relevant details with our partners (e.g. drivers, activity operators) so they can provide the service.</li>
        <li>Send booking confirmations, updates, and important notices about your trips.</li>
        <li>Respond to your questions and support requests.</li>
        <li>Improve our website, services, and user experience.</li>
        <li>Detect and prevent fraud, abuse, and security issues.</li>
        <li>Comply with laws and enforce our terms.</li>
        <li>With your consent, send marketing (e.g. offers, new activities). You can opt out at any time.</li>
      </ul>

      <h2 className="h5 text-primary mt-4 mb-2">3. Sharing Your Information</h2>
      <p>
        We may share your information with:
      </p>
      <ul className="legal-list pl-4">
        <li><strong>Service and activity partners:</strong> drivers, operators, and accommodation providers who need it to perform your booking.</li>
        <li><strong>Payment providers:</strong> to process payments (e.g. Khalti, eSewa, card processors). Their use of your data is governed by their own privacy policies.</li>
        <li><strong>Support and communication tools:</strong> e.g. email and SMS services that help us communicate with you.</li>
        <li><strong>Analytics and hosting:</strong> services that help us run, monitor, and improve our website (e.g. hosting, error tracking). We choose providers that commit to appropriate data protection.</li>
        <li><strong>Legal and safety:</strong> authorities or other parties when required by law, or to protect rights, safety, or property.</li>
      </ul>
      <p>
        We do not sell your personal information to third parties for their marketing.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">4. Cookies, Analytics &amp; Advertising</h2>
      <p>
        We use cookies and similar technologies (e.g. local storage) to keep you logged in, remember preferences, understand how the site is used, and improve it. You can control cookies through your browser settings. Some features may not work fully if you disable them.
      </p>
      <p>
        We may use <strong>Google Analytics</strong> to measure traffic and <strong>Google AdSense</strong> to show advertisements. Google and its partners may use cookies to serve ads based on your visits to this and other websites. You can opt out of personalized advertising by visiting{' '}
        <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>
        {' '}or{' '}
        <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">aboutads.info</a>.
        See Google&apos;s privacy policy at{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">5. Data Retention and Security</h2>
      <p>
        We keep your information for as long as needed to provide our services, handle disputes, and meet legal and regulatory requirements. We use reasonable technical and organisational measures to protect your data; no system is fully secure, and we cannot guarantee absolute security.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">6. Your Rights and Choices</h2>
      <p>
        Depending on where you live, you may have the right to access, correct, delete, or restrict use of your personal information, or to object to certain processing. You can often update your account details in your profile. To exercise other rights or ask questions, contact us (see below). If you are in the EU/EEA, you also have the right to lodge a complaint with a supervisory authority.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">7. Children</h2>
      <p>
        Our services are not directed at children under 16. We do not knowingly collect personal information from children. If you believe we have collected such information, please contact us and we will delete it.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">8. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post the new version on this page and update the &quot;Last updated&quot; date. Continued use of our services after changes means you accept the updated policy. We encourage you to review it periodically.
      </p>

      <h2 className="h5 text-primary mt-4 mb-2">9. Contact Us</h2>
      <p>
        For privacy-related questions or requests, contact us via <Link to="/support">Customer Support</Link> or the contact options on our website (e.g. live chat, email).
      </p>

      <hr className="my-4" />
      <p className="mb-0">
        <Link to="/" className="btn btn-outline-primary btn-sm">Back to Home</Link>
        <Link to="/about" className="btn btn-outline-secondary btn-sm ml-2">About Us</Link>
        <Link to="/terms" className="btn btn-outline-secondary btn-sm ml-2">Terms of Service</Link>
      </p>
    </div>
  </div>
);

export default PrivacyPolicy;
